import { memo } from "react"
import { FiSearch } from "react-icons/fi"

import { ClassTemplateActionsMenu } from "@/components/layout/Common/ClassTemplateActionsMenu"
import type { ClassTemplateResponse } from "@/types/class_template"
import { EmptyState, Flex, Table, VStack } from "@chakra-ui/react"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

interface ClassTemplateListProps {
  classTemplates: ClassTemplateResponse[]
}

const ClassTemplateList = memo(({ classTemplates }: ClassTemplateListProps) => {
  if (!classTemplates?.length) {
    return (
      <EmptyState.Root>
        <EmptyState.Content>
          <EmptyState.Indicator>
            <FiSearch />
          </EmptyState.Indicator>
          <VStack textAlign="center">
            <EmptyState.Title>No classTemplates yet</EmptyState.Title>
            <EmptyState.Description>
              Create a new classTemplate to see it listed here.
            </EmptyState.Description>
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>
    )
  }

  return (
    <Table.Root size={{ base: "sm", md: "md" }}>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader w="sm">ID</Table.ColumnHeader>
          <Table.ColumnHeader>Name</Table.ColumnHeader>
          <Table.ColumnHeader>Slug</Table.ColumnHeader>
          <Table.ColumnHeader>Description</Table.ColumnHeader>
          <Table.ColumnHeader>Program Type</Table.ColumnHeader>
          <Table.ColumnHeader>Instructor Id</Table.ColumnHeader>
          <Table.ColumnHeader>Swim Level</Table.ColumnHeader>
          <Table.ColumnHeader>Location</Table.ColumnHeader>
          <Table.ColumnHeader>Day Of Week</Table.ColumnHeader>
          <Table.ColumnHeader>Start Time</Table.ColumnHeader>
          <Table.ColumnHeader>End Time</Table.ColumnHeader>
          <Table.ColumnHeader>Duration Minutes</Table.ColumnHeader>
          <Table.ColumnHeader>Max Students</Table.ColumnHeader>
          <Table.ColumnHeader>Min Students</Table.ColumnHeader>
          <Table.ColumnHeader>Current Enrollment</Table.ColumnHeader>
          <Table.ColumnHeader>Waitlist Enabled</Table.ColumnHeader>
          <Table.ColumnHeader>Total Price (10 classes)</Table.ColumnHeader>
          <Table.ColumnHeader>Prerequisites</Table.ColumnHeader>
          <Table.ColumnHeader>Notes</Table.ColumnHeader>
          <Table.ColumnHeader>Is Active</Table.ColumnHeader>
          <Table.ColumnHeader>Is Published</Table.ColumnHeader>
          <Table.ColumnHeader>Created By</Table.ColumnHeader>
          <Table.ColumnHeader>Created</Table.ColumnHeader>
          <Table.ColumnHeader>Updated By</Table.ColumnHeader>
          <Table.ColumnHeader>Updated</Table.ColumnHeader>
          <Table.ColumnHeader w="xs">Actions</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {classTemplates.map((classTemplate) => (
          <Table.Row key={classTemplate.id}>
            <Table.Cell truncate maxW="sm">
              {classTemplate.id}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.name, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.slug, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.description, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.program_type, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.instructor_id, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.swim_level, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.location, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.day_of_week, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.start_time, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.end_time, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.duration_minutes, "int")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.max_students, "int")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.min_students, "int")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.current_enrollment, "int")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.waitlist_enabled, "bool")}
            </Table.Cell>
            <Table.Cell>
              {classTemplate.price != null
                ? `$${(Number(classTemplate.price) * 10).toFixed(2)}`
                : "N/A"}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.prerequisites, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.notes, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.is_active, "bool")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.is_published, "bool")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.created_by, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.created_at, "datetime")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.updated_by, "str")}
            </Table.Cell>
            <Table.Cell>
              {formatFieldValue(classTemplate.updated_at, "datetime")}
            </Table.Cell>
            <Table.Cell>
              <Flex justify="flex-end">
                <ClassTemplateActionsMenu classTemplate={classTemplate} />
              </Flex>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
})

ClassTemplateList.displayName = "ClassTemplateList"

export default ClassTemplateList
